import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useForm, Controller } from 'react-hook-form';
import { debounce } from 'debounce';
import { GiCheckMark } from 'react-icons/gi';

import AuthLayout from '../components/AuthLayout';
import Button from '../components/inputs/Button';
import TextInput from '../components/inputs/TextInput';
import ThemeLink from '../components/ThemeLink';
import LoadingSpinner from '../components/LoadingSpinner';

import { validateEmail, validatePassword } from '../helpers/validators';
import CheckboxInput from '../components/inputs/CheckboxInput';
import ExternalThemeLink from '../components/ExternalThemeLink';
import ReactHookFormError from '../components/errors/ReactHookFormError';
import ErrorText from '../components/ErrorText';
import { useLocation, useNavigate } from 'react-router-dom';

export default function SignUp() {
  const { signup } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    control,
    trigger,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
      passwordConfirm: '',
      companyName: '',
      agreeToTerms: false,
    },
  });

  const [signupError, setSignupError] = useState();
  const [signingUp, setSigningUp] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';

  function onSubmit(data) {
    setSigningUp(true);
    signup(data)
      .then(() => {
        navigate('/signin', { replace: true });
      })
      .catch((err) => {
        setSignupError(err.message);
      })
      .finally(() => {
        setSigningUp(false);
      });
  }

  const debouncedTriggerValidation = debounce((name) => trigger(name), 700);

  return (
    <AuthLayout
      heading={
        <>
          <h3>
            Ready to reach your next customer?{' '}
            <span className="text-primary">Let's go!</span>
          </h3>
        </>
      }
    >
      {signingUp && <LoadingSpinner message="Signing you up..." />}
      {!signingUp && (
        <form
          className="flex flex-col text-center"
          onSubmit={handleSubmit(onSubmit)}
        >
          <h4>Register</h4>
          <Controller
            control={control}
            name="email"
            rules={{
              required: 'Email is required.',
              validate: (v) => validateEmail(v) || `${v} is not a valid email.`,
            }}
            render={({
              field: { ref, onChange, ...restField },
              fieldState: { error, isTouched, isDirty },
            }) => (
              <TextInput
                {...restField}
                ref={ref}
                className="sm-h:mb-2 mb-3"
                type="text"
                name="email"
                placeholder="Enter your work email"
                error={error}
                validated={Boolean(!error && (isTouched || isDirty))}
                onChange={(value) => {
                  onChange(value);
                  debouncedTriggerValidation('email');
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="password"
            rules={{
              required: 'Password is required.',
              validate: (v) =>
                validatePassword(v) ||
                'Password must be at least 8 characters.',
            }}
            render={({
              field: { ref, onChange, ...restField },
              fieldState: { error, isTouched, isDirty },
            }) => (
              <TextInput
                {...restField}
                ref={ref}
                className="sm-h:mb-2 mb-3"
                type="password"
                name="password"
                placeholder="Pick a password"
                error={error}
                validated={Boolean(!error && (isTouched || isDirty))}
                onChange={(value) => {
                  onChange(value);
                  debouncedTriggerValidation('password');
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="passwordConfirm"
            rules={{
              validate: (v) =>
                getValues('password') === v || 'Passwords do not match.',
            }}
            render={({
              field: { ref, onChange, ...restField },
              fieldState: { error, isTouched, isDirty },
            }) => (
              <TextInput
                {...restField}
                ref={ref}
                className="sm-h:mb-2 mb-3"
                type="password"
                placeholder="Confirm your password"
                error={error}
                validated={Boolean(!error && (isTouched || isDirty))}
                onChange={(value) => {
                  onChange(value);
                  debouncedTriggerValidation('passwordConfirm');
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="companyName"
            render={({ field: { ref, ...restField } }) => (
              <TextInput
                {...restField}
                ref={ref}
                className="sm-h:mb-2 mb-3"
                type="text"
                placeholder="Add company name"
              />
            )}
          />
          <div className="flex justify-center">
            <Controller
              control={control}
              name="agreeToTerms"
              rules={{
                required: 'You must agree to the Terms of Use.',
              }}
              render={({ field: { ref, ...restField } }) => (
                <label className="cursor-pointer">
                  <CheckboxInput {...restField} className="mr-2" /> I agree to
                  the{' '}
                </label>
              )}
            />
            <ExternalThemeLink
              className="ml-1"
              href="/terms"
              target="_blank"
              rel="noreferrer"
            >
              Terms of Use
            </ExternalThemeLink>
          </div>

          {signupError && (
            <ErrorText
              message={signupError}
              className="mt-3 -mb-9 sm-h:-mb-8"
            />
          )}
          <ReactHookFormError
            errors={errors}
            className="mt-3 -mb-9 sm-h:-mb-8"
          />
          <Button
            className="sm-h:mt-11 mt-14 w-80 mx-auto"
            type="submit"
            primary
          >
            Sign Up
          </Button>
          <p className="mt-1">
            Already have an account? <ThemeLink to="/signin">Sign In</ThemeLink>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}
